import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import Header from '../components/Header'
import BookingProgress from '../sections/Booking/BookingProgress'
import BookStep1 from '../sections/Booking/book-step1'
import BookStep2 from '../sections/Booking/book-step2'
import { bookingService } from '../api/services/bookingService'
import BookStep3 from '../sections/Booking/book-step3'
import BookStep4 from '../sections/Booking/book-step4'
import BookingSuccessPopup from '../sections/Booking/BookingSuccessPopup'
import type { BookingStop } from '../sections/Booking/BookingItineraryCard'
import type { Vehicle } from '../types/booking'
import { apiGetPrivateTourById } from '../api/privateTours/privateTourApi'
import { apiGetDayTripById } from '../api/dayTrips/dayTripApi'
import { apiGetSkiTransferById } from '../api/skiTransfers/skiTransferApi'
import { calculateRouteDistanceAndTiming } from '../services/googleRouteCalculation'
import { calculateTransferPrice } from '../constants/transferBooking'

type BookingStep = 1 | 2 | 3 | 4

export type BookingSearchData = {
  fromTitle: string
  fromAddress: string
  toTitle: string
  toAddress: string
  date: string
  time: string
  returnDate: string
  returnTime: string
  adults: number
  children: number
  extraBags: number
  durationHours?: number
  distanceKm?: number
  durationText?: string
  distanceText?: string
  fromLat?: number
  fromLng?: number
  toLat?: number
  toLng?: number
  position?: { lat: number; lng: number } | string
  keyword?: string
}

export type TravelerDetails = {
  name: string
  phone: string
  email: string
  bookingForSomeoneElse: boolean
  otherPersonName: string
  otherPersonPhone: string
}

export type ArrivalInfo = {
  type: 'flight' | 'train'
  airlineOrOperator: string
  number: string
  terminal: string
  time: string
}

export default function Booking() {
  const [searchParams] = useSearchParams()

  const vehicleParam = searchParams.get('vehicle')
  const serviceType = searchParams.get('serviceType') || ''
  const serviceId = searchParams.get('serviceId') || ''

  const initialServicePrice =
    Number(searchParams.get('servicePrice')) || 0

  const initialServiceTitle =
    searchParams.get('serviceTitle') || ''

  const [servicePrice, setServicePrice] =
    useState<number>(initialServicePrice)

  const [serviceTitle, setServiceTitle] =
    useState<string>(initialServiceTitle)

  const skipStops = true

  const durationParam =
    Number(searchParams.get('duration')) || 0

  const distanceParam =
    Number(searchParams.get('distance')) ||
    (durationParam ? durationParam * 40 : 80)

  const fromLatParam = searchParams.get('fromLat')
    ? Number(searchParams.get('fromLat'))
    : undefined

  const fromLngParam = searchParams.get('fromLng')
    ? Number(searchParams.get('fromLng'))
    : undefined

  const toLatParam = searchParams.get('toLat')
    ? Number(searchParams.get('toLat'))
    : undefined

  const toLngParam = searchParams.get('toLng')
    ? Number(searchParams.get('toLng'))
    : undefined

  const [searchData, setSearchData] =
    useState<BookingSearchData>(() => ({
      fromTitle:
        searchParams.get('fromTitle') || 'Vienna',

      fromAddress:
        searchParams.get('fromAddress') ||
        'Vienna, Austria',

      toTitle:
        searchParams.get('toTitle') ||
        (serviceType === 'hourly'
          ? ''
          : 'Salzburg'),

      toAddress:
        searchParams.get('toAddress') ||
        (serviceType === 'hourly'
          ? ''
          : 'Salzburg, Austria'),

      fromLat: fromLatParam,
      fromLng: fromLngParam,
      toLat: toLatParam,
      toLng: toLngParam,

      date:
        searchParams.get('date') ||
        '2026-05-22',

      time:
        searchParams.get('time') ||
        '09:00',

      returnDate:
        searchParams.get('returnDate') ||
        '',

      returnTime:
        searchParams.get('returnTime') ||
        '',

      adults:
        Number(searchParams.get('adults')) ||
        2,

      children:
        Number(searchParams.get('children')) ||
        0,

      extraBags:
        Number(searchParams.get('extraBags')) ||
        0,

      durationHours:
        durationParam || 2,

      distanceKm:
        distanceParam || 80,

      durationText: '',
      distanceText: '',
    }))

  const hasExplicitDistance =
    Boolean(searchParams.get('distance'))

  const hasExplicitFrom =
    Boolean(searchParams.get('fromTitle'))

  const hasExplicitTo =
    Boolean(searchParams.get('toTitle'))

  /*
   * Return the vehicle rate that belongs to
   * the current booking type.
   *
   * Day Trip -> dayTripPrice
   * Hourly   -> hourlyPrice
   * Other    -> basePrice
   */
  const getVehiclePriceForBookingType = (
    vehicle: Vehicle,
  ): number => {
    if (serviceType === 'daytrips') {
      return Number(vehicle.dayTripPrice ?? 0)
    }

    if (serviceType === 'hourly') {
      return Number(vehicle.hourlyPrice ?? 0)
    }

    return Number(
      vehicle.basePrice ??
        vehicle.price ??
        0,
    )
  }

  /*
   * Calculate the total vehicle price for the
   * current booking type.
   *
   * Day Trip:
   *   dayTripPrice
   *
   * Hourly:
   *   hourlyPrice × durationHours
   *
   * Normal transfer:
   *   basePrice + distance pricing
   */
  const getVehicleTotalPrice = (
    vehicle: Vehicle,
    durationHours?: number,
    distanceKm?: number,
    isTwoWay = false,
  ): number => {
    if (serviceType === 'daytrips') {
      return Math.round(
        getVehiclePriceForBookingType(vehicle),
      )
    }

    if (serviceType === 'hourly') {
      const hourlyRate =
        getVehiclePriceForBookingType(vehicle)

      const hours =
        Number(durationHours) > 0
          ? Number(durationHours)
          : 1

      return Math.round(
        hourlyRate * hours,
      )
    }

    const dist =
      Number(distanceKm) || 0

    const oneWay =
      calculateTransferPrice(
        getVehiclePriceForBookingType(
          vehicle,
        ),
        vehicle.pricePerKm ?? 0,
        dist,
      )

    return isTwoWay
      ? oneWay * 2
      : oneWay
  }

  // Auto-calculate driving distance and duration via Google Maps calculation
  useEffect(() => {
    if (
      serviceType === 'hourly' ||
      !searchData.fromTitle ||
      !searchData.toTitle
    ) {
      return
    }

    if (
      hasExplicitDistance &&
      !hasExplicitFrom &&
      !hasExplicitTo
    ) {
      return
    }

    let isMounted = true

    const calculateDistance = async () => {
      try {
        const origin = {
          title: searchData.fromTitle,
          address: searchData.fromAddress,
          lat: searchData.fromLat,
          lng: searchData.fromLng,
        }

        const destination = {
          title: searchData.toTitle,
          address: searchData.toAddress,
          lat: searchData.toLat,
          lng: searchData.toLng,
        }

        const result =
          await calculateRouteDistanceAndTiming(
            origin,
            destination,
          )

        if (
          isMounted &&
          result.distanceKm > 0
        ) {
          setSearchData((prev) => ({
            ...prev,
            distanceKm: result.distanceKm,
            distanceText: result.distanceText,
            durationText: result.durationText,
            durationHours: Math.max(
              1,
              Math.round(
                result.durationMinutes / 60,
              ),
            ),
          }))
        }
      } catch (err) {
        console.warn(
          'Failed to calculate route distance/timing:',
          err,
        )
      }
    }

    calculateDistance()

    return () => {
      isMounted = false
    }
  }, [
    searchData.fromTitle,
    searchData.fromAddress,
    searchData.toTitle,
    searchData.toAddress,
    searchData.fromLat,
    searchData.fromLng,
    searchData.toLat,
    searchData.toLng,
    serviceType,
  ])

  const [currentStep, setCurrentStep] =
    useState<BookingStep>(1)

  const [selectedVehicle, setSelectedVehicle] =
    useState<Vehicle | null>(null)

  const [stops, setStops] =
    useState<BookingStop[]>([
      {
        id: 1,
        location: '',
        type: 'Sightseeing',
        duration: '45 min',
      },
    ])

  const [travelerDetails, setTravelerDetails] =
    useState<TravelerDetails>({
      name: '',
      phone: '+43',
      email: '',
      bookingForSomeoneElse: false,
      otherPersonName: '',
      otherPersonPhone: '+43',
    })

  const [arrivalInfo, setArrivalInfo] =
    useState<ArrivalInfo>({
      type: 'flight',
      airlineOrOperator: 'Search Airline',
      number: '',
      terminal: 'Terminal 1',
      time: '09:35',
    })

  const [specialRequirements, setSpecialRequirements] =
    useState('')

  const [selectedOptions, setSelectedOptions] =
    useState<string[]>([])

  const [successOpen, setSuccessOpen] =
    useState(false)

  const [bookingId, setBookingId] =
    useState<string | undefined>(undefined)

  /*
   * Load the vehicle selected from the URL.
   *
   * Day Trip:
   *   dayTripPrice
   *
   * Hourly:
   *   hourlyPrice × durationHours
   *
   * Other:
   *   basePrice + distance pricing
   */
  useEffect(() => {
    if (!vehicleParam) {
      return
    }

    const loadVehicle = async () => {
      const vehicles =
        await bookingService.fetchVehicles()

      const matched = vehicles.find(
        (v) =>
          v.id.toLowerCase() ===
          vehicleParam.toLowerCase(),
      )

      if (!matched) {
        return
      }

      const totalPassengers = Math.max(
        1,
        Number(searchData.adults || 0) +
          Number(searchData.children || 0),
      )

      if (Number(matched.passengers ?? 0) < totalPassengers) {
        return
      }

      const isTwoWay =
        Boolean(searchData.returnDate)

      const vehicleTotalPrice =
        getVehicleTotalPrice(
          matched,
          searchData.durationHours,
          searchData.distanceKm,
          isTwoWay,
        )

      setSelectedVehicle({
        ...matched,
        price: vehicleTotalPrice,
      })

      setCurrentStep(
        skipStops ? 3 : 2,
      )
    }

    loadVehicle()
  }, [
    vehicleParam,
    skipStops,
    serviceType,
    searchData.distanceKm,
    searchData.durationHours,
    searchData.returnDate,
    searchData.adults,
    searchData.children,
  ])

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'auto',
    })
  }, [currentStep])

  /*
   * Load the service price if it wasn't already
   * provided through the booking URL.
   */
  useEffect(() => {
    if (
      servicePrice > 0 ||
      !serviceId
    ) {
      return
    }

    const loadService = async () => {
      try {
        if (
          serviceType === 'privatetours'
        ) {
          const res =
            await apiGetPrivateTourById(
              serviceId,
            )

          if (res.data) {
            setServicePrice(
              res.data.price,
            )

            if (!serviceTitle) {
              setServiceTitle(
                res.data.title,
              )
            }
          }
        } else if (
          serviceType === 'daytrips'
        ) {
          const res =
            await apiGetDayTripById(
              serviceId,
            )

          if (res.data) {
            setServicePrice(
              res.data.price,
            )

            if (!serviceTitle) {
              setServiceTitle(
                res.data.title,
              )
            }
          }
        } else if (
          serviceType === 'skitransfers'
        ) {
          const res =
            await apiGetSkiTransferById(
              serviceId,
            )

          if (res.data) {
            setServicePrice(
              res.data.base_price,
            )

            if (!serviceTitle) {
              setServiceTitle(
                res.data.title,
              )
            }
          }
        }
      } catch (err) {
        console.error(
          'Failed to load service pricing:',
          err,
        )
      }
    }

    loadService()
  }, [
    serviceType,
    serviceId,
    servicePrice,
    serviceTitle,
  ])

  const resolvedServiceTitle =
    serviceTitle ||
    (
      serviceType === 'privatetours'
        ? 'Private Tour'
        : serviceType === 'daytrips'
          ? 'Day Trip'
          : serviceType === 'skitransfers'
            ? 'Ski Transfer'
            : ''
    )

  const handleStepChange = (
    step: number,
  ) => {
    if (
      step >= 1 &&
      step <= 4
    ) {
      setCurrentStep(
        step as BookingStep,
      )
    }
  }

  /*
   * Handle vehicle selection from BookStep1.
   *
   * Day Trip:
   *   vehicle.dayTripPrice
   *
   * Hourly:
   *   vehicle.hourlyPrice × durationHours
   *
   * Other:
   *   basePrice + existing distance calculation
   */
  const handleVehicleSelection = (
    vehicle: Vehicle,
  ) => {
    const totalPassengers = Math.max(
      1,
      Number(searchData.adults || 0) +
        Number(searchData.children || 0),
    )

    if (Number(vehicle.passengers ?? 0) < totalPassengers) {
      return
    }

    const isTwoWay =
      Boolean(searchData.returnDate)

    const vehicleTotalPrice =
      getVehicleTotalPrice(
        vehicle,
        searchData.durationHours,
        searchData.distanceKm,
        isTwoWay,
      )

    setSelectedVehicle({
      ...vehicle,
      price: vehicleTotalPrice,
    })

    setCurrentStep(
      skipStops ? 3 : 2,
    )
  }

  /*
   * Keep the selected vehicle price reactive
   * when distance, duration, or return-trip
   * settings change. Also clears selection if
   * passengers exceed vehicle capacity.
   *
   * Day Trips:
   *   dedicated dayTripPrice
   *
   * Hourly:
   *   hourlyPrice × durationHours
   *
   * Normal transfers:
   *   distance-based calculation
   */
  useEffect(() => {
    setSelectedVehicle((prev) => {
      if (!prev) {
        return null
      }

      const totalPassengers = Math.max(
        1,
        Number(searchData.adults || 0) +
          Number(searchData.children || 0),
      )

      if (Number(prev.passengers ?? 0) < totalPassengers) {
        return null
      }

      const isTwoWay =
        Boolean(searchData.returnDate)

      const vehicleTotalPrice =
        getVehicleTotalPrice(
          prev,
          searchData.durationHours,
          searchData.distanceKm,
          isTwoWay,
        )

      return {
        ...prev,
        price: vehicleTotalPrice,
      }
    })
  }, [
    searchData.distanceKm,
    searchData.durationHours,
    searchData.returnDate,
    searchData.adults,
    searchData.children,
    serviceType,
  ])

  return (
    <main className="min-h-screen overflow-x-hidden bg-[linear-gradient(110deg,#F9FCFB_0%,#EEF8FA_58%,#B8DDE5_100%)]">
      <div className="mx-auto max-w-page px-4 sm:px-6">
        <Header />
      </div>

      <div className="mx-auto mt-2 max-w-[1180px] px-4 sm:px-6 lg:px-8">
        <BookingProgress
          currentStep={currentStep}
          onStepChange={handleStepChange}
          skipStops={skipStops}
        />
      </div>

      {currentStep === 1 && (
        <BookStep1
          onNext={handleVehicleSelection}
          searchData={searchData}
          onSearchDataChange={
            setSearchData
          }
          servicePrice={servicePrice}
          serviceTitle={
            resolvedServiceTitle
          }
          serviceType={serviceType}
        />
      )}

      {currentStep === 2 && (
        <BookStep2
          selectedVehicle={
            selectedVehicle
          }
          onBack={() =>
            setCurrentStep(1)
          }
          onNext={() =>
            setCurrentStep(3)
          }
          searchData={searchData}
          stops={stops}
          onStopsChange={setStops}
          onEditRoute={() =>
            setCurrentStep(1)
          }
          onSearchDataChange={
            setSearchData
          }
          servicePrice={servicePrice}
          serviceTitle={
            resolvedServiceTitle
          }
          serviceType={serviceType}
        />
      )}

      {currentStep === 3 && (
        <BookStep3
          selectedVehicle={
            selectedVehicle
          }
          onBack={() =>
            setCurrentStep(
              skipStops ? 1 : 2,
            )
          }
          onNext={() =>
            setCurrentStep(4)
          }
          searchData={searchData}
          onSearchDataChange={
            setSearchData
          }
          stops={stops}
          onEditRoute={() =>
            setCurrentStep(1)
          }
          travelerDetails={
            travelerDetails
          }
          setTravelerDetails={
            setTravelerDetails
          }
          arrivalInfo={arrivalInfo}
          setArrivalInfo={
            setArrivalInfo
          }
          specialRequirements={
            specialRequirements
          }
          setSpecialRequirements={
            setSpecialRequirements
          }
          selectedOptions={
            selectedOptions
          }
          setSelectedOptions={
            setSelectedOptions
          }
          servicePrice={servicePrice}
          serviceTitle={
            resolvedServiceTitle
          }
          serviceType={serviceType}
        />
      )}

      {currentStep === 4 && (
        <BookStep4
          selectedVehicle={
            selectedVehicle
          }
          onBack={() =>
            setCurrentStep(3)
          }
          onComplete={(
            paymentMethod,
            bId,
          ) => {
            console.log(
              'Booking completed using:',
              paymentMethod,
            )

            setBookingId(bId)
            setSuccessOpen(true)
          }}
          searchData={searchData}
          stops={stops}
          onEditRoute={() =>
            setCurrentStep(1)
          }
          onSearchDataChange={
            setSearchData
          }
          travelerDetails={
            travelerDetails
          }
          arrivalInfo={arrivalInfo}
          specialRequirements={
            specialRequirements
          }
          selectedOptions={
            selectedOptions
          }
          serviceType={serviceType}
          serviceId={serviceId}
          servicePrice={servicePrice}
          serviceTitle={
            resolvedServiceTitle
          }
        />
      )}

      <BookingSuccessPopup
        isOpen={successOpen}
        onClose={() =>
          setSuccessOpen(false)
        }
        bookingId={bookingId}
      />
    </main>
  )
}